import { type ReactNode, type Ref } from "react";

import {
  TextArea as ReactAriaTextArea,
  type TextAreaProps as ReactAriaTextAreaProps,
  type TextFieldProps as ReactAriaTextFieldProps,
  TextField as ReactAriaTextField,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { cn, customTv } from "#utils";

const textAreaStyles = customTv({
  base: `
    text-label-medium text-text-primary py-xs outline-none border border-outline-input-enabled bg-primary rounded-1 px-lg leading-md-20
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    focus:border-outline-input-focus focus:ring-1 focus:ring-outline-input-focus
    disabled:border-outline-input-disabled disabled:cursor-not-allowed disabled:text-text-disabled disabled:bg-background-primary disabled:hover:ring-0 disabled:hover:border-outline-input-disabled
    resize-y min-h-[4.5rem]
    placeholder:text-text-secondary disabled:placeholder:text-text-disabled
  `,
  variants: {
    variant: {
      primary: "",
    },
    autoResize: {
      true: "min-h-fit [field-sizing:content]",
    },
    isReadOnly: {
      true: "!cursor-text !bg-background-secondary !text-text-primary !border-transparent hover:ring-0 focus:ring-0",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
  },
});

interface TextAreaInputProps extends ReactAriaTextAreaProps {
  variant?: "primary";
  className?: string;
}

export interface TextAreaProps extends ReactAriaTextFieldProps {
  textAreaProps?: TextAreaInputProps;
  label?: string;
  tooltipText?: ReactNode;
  assistiveText?: ReactNode;
  className?: string;
  /**
   * Grow and shrink the field to fit its content (via CSS `field-sizing`),
   * bounded by `rows` (min). Browsers without `field-sizing` support fall back to the manual resize handle.
   */
  autoResize?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

export function TextArea({
  textAreaProps,
  label,
  tooltipText,
  className,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  isRequired,
  autoResize,
  ref,
  ...rest
}: TextAreaProps) {
  return (
    <ReactAriaTextField
      className={cn("flex flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      {...rest}
    >
      <FormFieldHeader
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
      />
      <ReactAriaTextArea
        {...textAreaProps}
        className={textAreaStyles({
          variant: textAreaProps?.variant,
          className: textAreaProps?.className,
          autoResize,
          isReadOnly,
          isInvalid,
        })}
        placeholder={textAreaProps?.placeholder || "Type here..."}
        ref={ref}
        rows={textAreaProps?.rows ?? 2}
      />
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </ReactAriaTextField>
  );
}
